import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import { ReactNode } from "react";

import APPrintablePage from "../../../../components/APPrintablePage";
import APCalculation from "../../../../components/calculations/APCalculation";
import APCalculationInput from "../../../../components/calculations/APCalculationInput";
import APCalculationOutput from "../../../../components/calculations/APCalculationOutput";
import APPeriodicCalculations from "../../../../components/calculations/APPeriodicCalculation";
import { toDollars } from "../../../../helpers/currencies";
import { FinanceComparisonCalculations as FinanceComparisonCalculationsType } from "../../../../types/Calculation";

type DisplayedFields = {
  onroadVehiclePrice?: boolean;
  residualIncGst?: boolean;
  netCostOfPurchase?: boolean;

  cashRate?: boolean;
  termMths?: boolean;
  interestForgone?: boolean;

  runningCosts?: boolean;
  lifetimeCost?: boolean;
};

export const FINANCE_COMPARISON_CASH_ALL_FIELDS: DisplayedFields = {
  onroadVehiclePrice: true,
  residualIncGst: true,
  netCostOfPurchase: true,

  cashRate: true,
  termMths: true,
  interestForgone: true,

  runningCosts: true,
  lifetimeCost: true,
};

const FinanceComparisonCashCalculations = ({
  calculations,
  displayedFields,
  pageLinks,
}: {
  calculations: FinanceComparisonCalculationsType;
  displayedFields: DisplayedFields;
  pageLinks?: ReactNode;
}) => {
  const { inputs, cash } = calculations;

  return (
    <APPrintablePage
      topContent={
        <Stack spacing={2}>
          {pageLinks}
          <Box>
            {displayedFields.onroadVehiclePrice && (
              <APCalculationInput
                name="Onroad Vehicle Price"
                value={inputs.onroadVehiclePrice}
                currency
              />
            )}
            {displayedFields.residualIncGst && (
              <APCalculationInput
                name="Residual Inc GST"
                value={inputs.residual.incGst}
                currency
              />
            )}
            {displayedFields.netCostOfPurchase && (
              <APCalculationOutput
                calculation={cash.netCostOfPurchase}
                currency
              />
            )}
          </Box>
          <Box>
            {displayedFields.cashRate && (
              <APCalculationInput
                name="Cash Rate"
                value={inputs.cashInterestRate}
                percentage
              />
            )}
            {displayedFields.termMths && (
              <APCalculationInput name="Term Months" value={inputs.termMths} />
            )}
            {displayedFields.interestForgone && (
              <APCalculationOutput
                calculation={cash.interestForgone}
                currency
              />
            )}
          </Box>
          <Box>
            {displayedFields.runningCosts && (
              <APCalculationInput
                name="Running Costs"
                value={inputs.runningCosts.total}
                currency
              />
            )}
            {displayedFields.lifetimeCost && (
              <APCalculation
                name={cash.lifetimeCost.name}
                value={toDollars(cash.lifetimeCost.result.total, 2)}
                tooltip={cash.lifetimeCost.formula}
              />
            )}
          </Box>
        </Stack>
      }
      bottomContent={
        <Box sx={{ maxWidth: 750 }}>
          {displayedFields.lifetimeCost && (
            <APPeriodicCalculations
              calculations={[
                {
                  name: "Lifetime Cost",
                  amounts: cash.lifetimeCost.result,
                },
              ]}
            />
          )}
        </Box>
      }
    />
  );
};

export default FinanceComparisonCashCalculations;
